/**
 * Speaker sliders — the quote card and the testimonials band.
 *
 * One implementation for both. They look nothing alike, so appearance stays in
 * each section's own classes; everything here is the mechanism, bound to the
 * generic `dn-slider` markup:
 *
 *   .dn-slider            the root, carrying is-enhanced / is-measuring
 *     .dn-slider__track   the element whose height is animated
 *       .dn-slider__slide one per item, is-current on the visible one
 *     [hidden] nav        revealed once this file runs
 *       [data-dn-slider-step="-1|1"]
 *
 * Every slide ships in the markup and is visible by default. This file adds
 * `is-enhanced`, which is what collapses the track to one slide at a time and
 * reveals the arrows — so a browser that never runs it shows a readable list
 * rather than one item and two dead buttons.
 *
 * HEIGHTS ARE MEASURED, NOT INFERRED. A hidden slide reports a height of zero,
 * so animating towards "whatever the next slide reflows to" gives the container
 * nothing real to travel to and the transition fights itself. `is-measuring`
 * puts every slide back in flow for the length of a synchronous read, so each
 * is measured as it will actually be; no paint happens in between, so the
 * stacked state is never seen.
 */

const SLIDER = '.dn-slider';
const TRACK = '.dn-slider__track';
const SLIDE = '.dn-slider__slide';
const NAV = '.dn-slider__nav';

const SWIPE_THRESHOLD = 50;
const RESIZE_DEBOUNCE = 150;

/**
 * Whether the visitor has asked for reduced motion.
 *
 * @return {boolean} True when animation should be skipped.
 */
function reducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

/**
 * Re-run an animation that may already be on the element.
 *
 * Removing the class and reading a layout property forces the browser to flush
 * the removal, so pressing the same arrow twice quickly plays the animation
 * again rather than doing nothing.
 *
 * @param {HTMLElement} element   The element to animate.
 * @param {string}      className The animation class.
 */
function restartAnimation( element, className ) {
	element.classList.remove( className );
	void element.offsetWidth;
	element.classList.add( className );

	element.addEventListener(
		'animationend',
		() => element.classList.remove( className ),
		{ once: true }
	);
}

/**
 * Wire one slider.
 *
 * @param {HTMLElement} root The slider root.
 */
function initSlider( root ) {
	const track = root.querySelector( TRACK );
	const slides = Array.from( root.querySelectorAll( SLIDE ) );

	if ( ! track || ! slides.length ) {
		return;
	}

	root.classList.add( 'is-enhanced' );

	const nav = root.querySelector( NAV );

	let heights = [];
	let current = -1;

	function measure() {
		root.classList.add( 'is-measuring' );
		heights = slides.map( ( slide ) => slide.getBoundingClientRect().height );
		root.classList.remove( 'is-measuring' );
	}

	/**
	 * Set the track to a measured height.
	 *
	 * @param {number}  index   Index into slides.
	 * @param {boolean} animate Whether to transition to it.
	 */
	function applyHeight( index, animate ) {
		if ( ! heights[ index ] ) {
			return;
		}

		if ( animate ) {
			track.style.height = `${ heights[ index ] }px`;
			return;
		}

		// Suppress the stylesheet's transition for a height that should simply
		// be, rather than travel — first paint, a resize, a font swap.
		track.style.transition = 'none';
		track.style.height = `${ heights[ index ] }px`;
		void track.offsetHeight;
		track.style.removeProperty( 'transition' );
	}

	/**
	 * Show one slide.
	 *
	 * @param {number} index     Index into slides.
	 * @param {number} direction -1, 1, or 0 for the initial show.
	 */
	function show( index, direction ) {
		const previous = current;
		const animate = Boolean( direction ) && ! reducedMotion();

		current = index;

		slides.forEach( ( slide, i ) => {
			slide.classList.toggle( 'is-current', i === index );
		} );

		applyHeight( index, animate );

		if ( ! animate || previous < 0 || previous === index ) {
			return;
		}

		// One property drives both halves: the incoming slide comes in from the
		// side the arrow points, the outgoing leaves towards the other.
		root.style.setProperty( '--dn-slide-shift', direction > 0 ? '24px' : '-24px' );

		restartAnimation( slides[ index ], 'is-entering' );
		restartAnimation( slides[ previous ], 'is-leaving' );
	}

	measure();
	show( 0, 0 );

	/*
	 * Web fonts change how tall every slide is and usually land after this runs.
	 * Without a re-measure the track keeps the fallback font's heights and either
	 * clips or gaps once the real face arrives.
	 */
	if ( document.fonts && document.fonts.ready ) {
		document.fonts.ready.then( () => {
			measure();
			applyHeight( current, false );
		} );
	}

	let resizeTimer = null;

	window.addEventListener( 'resize', () => {
		window.clearTimeout( resizeTimer );

		resizeTimer = window.setTimeout( () => {
			measure();
			applyHeight( current, false );
		}, RESIZE_DEBOUNCE );
	} );

	// A single slide needs no controls, and the markup does not render them.
	if ( ! nav || slides.length < 2 ) {
		return;
	}

	nav.hidden = false;

	/**
	 * Move by a number of slides, wrapping at both ends.
	 *
	 * @param {number} by -1 or 1.
	 */
	function step( by ) {
		show( ( current + by + slides.length ) % slides.length, by );
	}

	nav.addEventListener( 'click', ( event ) => {
		const button = event.target.closest( '[data-dn-slider-step]' );

		if ( ! button ) {
			return;
		}

		event.preventDefault();
		step( parseInt( button.getAttribute( 'data-dn-slider-step' ), 10 ) || 1 );
	} );

	// Arrow keys, but only while focus is inside this slider — otherwise it
	// would hijack the arrow keys for the whole page.
	root.addEventListener( 'keydown', ( event ) => {
		if ( 'ArrowLeft' === event.key ) {
			event.preventDefault();
			step( -1 );
		} else if ( 'ArrowRight' === event.key ) {
			event.preventDefault();
			step( 1 );
		}
	} );

	let touchStartX = null;

	root.addEventListener(
		'touchstart',
		( event ) => {
			touchStartX = event.changedTouches[ 0 ].clientX;
		},
		{ passive: true }
	);

	root.addEventListener(
		'touchend',
		( event ) => {
			if ( null === touchStartX ) {
				return;
			}

			const delta = event.changedTouches[ 0 ].clientX - touchStartX;

			touchStartX = null;

			if ( Math.abs( delta ) < SWIPE_THRESHOLD ) {
				return;
			}

			step( delta < 0 ? 1 : -1 );
		},
		{ passive: true }
	);
}

/**
 * Keep each quote's closing mark on the same line as the quote's last word.
 *
 * The mark is an inline-block <span> holding an SVG, appended after the text by
 * speaker_section_quotes(), and Chrome will break a line in front of an inline
 * block whatever precedes it — the U+2060 word joiner the server puts there is
 * honoured between characters but not before an atomic inline. So on some
 * slides the mark sat alone on the last line (Chris Barton's card, reported by
 * the client).
 *
 * The fix moves the last word into a `white-space: nowrap` span together with
 * the mark: a line can still break before that word, never between the word
 * and the mark. Done in the DOM rather than the PHP because the quote is
 * third-party HTML — its last word may sit inside an <em> the mark sits
 * outside, and splitting that safely is a DOM job. The mark moving inside such
 * an element changes nothing about how it draws.
 *
 * Presentational only, and run before any slider measures its slides, so the
 * heights it records already include the reflow. Idempotent: a mark already
 * glued is left alone.
 */
function glueClosingMarks() {
	document.querySelectorAll( '.dn-speaker__quote-close' ).forEach( ( mark ) => {
		if ( mark.parentElement && mark.parentElement.classList.contains( 'dn-speaker__quote-glue' ) ) {
			return;
		}

		// The text that ends right before the mark, descending into the last
		// child of any element in the way (an <em>, a <strong>).
		let node = mark.previousSibling;

		while ( node && 1 === node.nodeType && node.lastChild ) {
			node = node.lastChild;
		}

		if ( ! node || 3 !== node.nodeType ) {
			return;
		}

		// Break only at ordinary whitespace: a no-break space is already
		// "keep together", so the word runs back past it.
		const text = node.data;
		const split = text.search( /[ \t\n\r\f](?=[^ \t\n\r\f]*$)/ );
		const start = -1 === split ? 0 : split + 1;

		const glue = document.createElement( 'span' );

		glue.className = 'dn-speaker__quote-glue';
		glue.textContent = text.slice( start );
		node.data = text.slice( 0, start );
		node.parentNode.insertBefore( glue, node.nextSibling );
		glue.appendChild( mark );
	} );
}

function init() {
	glueClosingMarks();
	document.querySelectorAll( SLIDER ).forEach( initSlider );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
